/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { PlusOutlined } from '@ant-design/icons'
import { Input as AntInput, InputRef } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import Text from 'src/components/ts-components/Text'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { normalizeOptionLabel } from '@sportninja/common/utils/optionLabel'
import { useRef, useEffect, useState } from 'react'
import { Option, generateId } from './types'
import OptionRow from './OptionRow'
import {
  MAX_OPTIONS,
  MAX_OPTION_LABEL_LENGTH,
  optionsToText,
  parseOptionText,
} from './optionListText'

const { TextArea } = AntInput

type EntryMode = 'rows' | 'text'

/**
 * Button copy, decided with James and Jeff (Slack, 2026-07-28): "Text" alone did
 * not say what the mode does. "Text Import" was the other candidate and is a
 * one-line change here if it is preferred later.
 */
const MODE_LABELS: Record<EntryMode, string> = {
  rows: 'Rows',
  text: 'Paste List',
}

/** Rows mode starts scrolling rather than pushing the modal's buttons offscreen. */
const ROWS_SCROLL_FROM = 8

/**
 * The empty textarea is the only place that can teach the format, so it does:
 * what a line means, that a whole list can be pasted, a worked example whose
 * entries are multi-word (so it is obvious a line is ONE option, spaces and
 * all), and what gets tidied up automatically.
 *
 * Deliberately sport-neutral — a dropdown is generic and this same field is used
 * for jersey sizes, divisions and referral sources as much as for venues.
 * Sized to sit inside minRows: 8.
 */
const PASTE_PLACEHOLDER = [
  'One option per line — paste a whole list at once, straight from a spreadsheet column or a document.',
  '',
  'For example:',
  'Youth Small',
  'Youth Large',
  'Adult Medium',
  '',
  'Blank lines and repeated options are removed for you.',
].join('\n')

interface OptionEditorProps {
  options: Option[]
  onChange: (options: Option[]) => void
}

/**
 * Option list editor with two interchangeable entry modes.
 *
 * Rows is the original one-input-per-option editor. Text swaps in a textarea
 * holding one label per line, so a long list (Adray's 105 rinks) can be pasted
 * in one go instead of typed a row at a time.
 *
 * The bound errors are surfaced here in BOTH modes: without that, a manually
 * authored 192-character row or a 501st option would merely grey out Save with
 * no explanation. The parser itself never truncates — see optionListText.
 */
const OptionEditor = ({ options, onChange }: OptionEditorProps) => {
  'use memo'

  const inputRefs = useRef<{ [key: string]: InputRef | null }>({})
  const lastAddedOptionId = useRef<string | null>(null)
  const [mode, setMode] = useState<EntryMode>('rows')
  // The textarea is uncontrolled with respect to `options`: it keeps the user's
  // raw text verbatim so the caret never jumps mid-edit, and pushes the PARSED
  // options upward on every change.
  const [text, setText] = useState('')
  // Whether `text` has been edited since the mode was entered. Seeding it from
  // the existing options is not an edit — see switchMode.
  const [textEdited, setTextEdited] = useState(false)

  const handleOptionChange = (optionId: string, label: string) => {
    onChange(
      options.map((option) =>
        option.id === optionId ? { ...option, label } : option
      )
    )
  }

  const handleDeleteOption = (optionId: string) => {
    if (options.length > 1) {
      delete inputRefs.current[optionId]
      onChange(options.filter((option) => option.id !== optionId))
    }
  }

  const handleAddOption = () => {
    const newOption: Option = { id: generateId(), label: '' }
    lastAddedOptionId.current = newOption.id
    onChange([...options, newOption])
  }

  const handleEnter = (optionId: string) => {
    const currentIndex = options.findIndex((option) => option.id === optionId)
    const isLastOption = currentIndex === options.length - 1

    // Only append when Enter lands on the last row and it has content.
    if (isLastOption && options[currentIndex]?.label.trim()) {
      handleAddOption()
    }
  }

  const setInputRef = (optionId: string) => (ref: InputRef | null) => {
    inputRefs.current[optionId] = ref
  }

  const switchMode = (next: EntryMode) => {
    // Re-seed from the normalized options every time Paste List is entered, so
    // the textarea reflects what is actually stored rather than a stale draft.
    if (next === 'text') {
      setText(optionsToText(options))
      // Seeding is NOT an edit: it does not call onChange, so nothing has been
      // normalized away yet and there is nothing truthful to report.
      setTextEdited(false)
    }
    setMode(next)
  }

  const handleTextChange = (value: string) => {
    setText(value)
    setTextEdited(true)
    onChange(parseOptionText(value).options)
  }

  // Auto-focus a newly added row.
  useEffect(() => {
    if (
      lastAddedOptionId.current &&
      inputRefs.current[lastAddedOptionId.current]
    ) {
      const input = inputRefs.current[lastAddedOptionId.current]
      if (input) {
        // setTimeout so the DOM has the new input before we focus it.
        setTimeout(() => {
          input.focus()
        }, 0)
      }
      lastAddedOptionId.current = null
    }
  }, [options])

  // Drop refs for options that no longer exist — pasting a new list replaces
  // every id, so without this the map grows without bound.
  useEffect(() => {
    const live = new Set(options.map((option) => option.id))
    Object.keys(inputRefs.current).forEach((id) => {
      if (!live.has(id)) {
        delete inputRefs.current[id]
      }
    })
  }, [options])

  // What the paste DISCARDED can only be read off the raw text — by definition
  // the normalized options no longer contain it. Gated on a real edit so the
  // report can never describe a normalization that did not happen.
  const pasteReport =
    mode === 'text' && textEdited ? parseOptionText(text) : null
  // Near-duplicates are RETAINED, so they stay visible in either mode.
  const { nearDuplicates } = parseOptionText(optionsToText(options))

  const validOptions = options.filter(({ label }) => label.trim())
  const overCap = validOptions.length > MAX_OPTIONS
  // Measured on the CANONICAL label, matching what convertOptionsToParameters
  // actually sends: padding and repeated inner whitespace never reach the
  // varchar(191) column, so neither may be counted against the bound here.
  const overLength = options.filter(
    ({ label }) => normalizeOptionLabel(label).length > MAX_OPTION_LABEL_LENGTH
  )

  // Deliberately does NOT repeat the option count — the header already shows it.
  const notes: string[] = []
  if (pasteReport) {
    if (pasteReport.duplicatesRemoved.length > 0) {
      notes.push(
        `${pasteReport.duplicatesRemoved.length} duplicate${
          pasteReport.duplicatesRemoved.length === 1 ? '' : 's'
        } removed`
      )
    }
    if (pasteReport.blankLinesIgnored > 0) {
      notes.push(
        `${pasteReport.blankLinesIgnored} blank ${
          pasteReport.blankLinesIgnored === 1 ? 'line' : 'lines'
        } ignored`
      )
    }
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 8px;
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        `}
      >
        <Text variant='paragraphSmall' weight='medium' color={colors.WHITE}>
          {`Options (${validOptions.length})`}
        </Text>
        <div
          role='group'
          aria-label='Option entry mode'
          css={css`
            display: flex;
            gap: 4px;
          `}
        >
          {(['rows', 'text'] as EntryMode[]).map((value) => (
            <button
              key={value}
              type='button'
              aria-pressed={mode === value}
              onClick={() => switchMode(value)}
              css={css`
                padding: 4px 12px;
                border-radius: 8px;
                cursor: pointer;
                white-space: nowrap;
                border: 1px ${mode === value ? 'solid' : 'dashed'}
                  ${mode === value ? colors.PRIMARY : colors.NEUTRAL_50};
                background: ${mode === value
                  ? 'rgba(255, 255, 255, 0.08)'
                  : 'transparent'};
                color: ${mode === value ? colors.WHITE : colors.NEUTRAL_50};
                &:hover {
                  border-color: ${colors.PRIMARY};
                }
              `}
            >
              {capitalizeWords(MODE_LABELS[value])}
            </button>
          ))}
        </div>
      </div>

      {mode === 'rows' ? (
        <>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 8px;
              ${options.length > ROWS_SCROLL_FROM
                ? 'max-height: 360px; overflow-y: auto;'
                : ''}
            `}
          >
            {options.map((option, index) => (
              <OptionRow
                key={option.id}
                option={option}
                index={index}
                canDelete={options.length > 1}
                onLabelChange={handleOptionChange}
                onDelete={handleDeleteOption}
                onEnter={handleEnter}
                setInputRef={setInputRef}
              />
            ))}
          </div>
          <button
            type='button'
            aria-label='Add option'
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 8px;
              border: 1px dashed ${colors.NEUTRAL_50};
              background: transparent;
              border-radius: 8px;
              cursor: pointer;
              &:hover {
                border-color: ${colors.PRIMARY};
                background: rgba(255, 255, 255, 0.05);
              }
            `}
            onClick={handleAddOption}
          >
            <PlusOutlined
              css={css`
                color: ${colors.NEUTRAL_50};
                font-size: 14px;
              `}
            />
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.NEUTRAL_50}
            >
              Add option
            </Text>
          </button>
        </>
      ) : (
        <TextArea
          value={text}
          onChange={(e) => handleTextChange(e.target.value)}
          autoSize={{ minRows: 8, maxRows: 20 }}
          aria-label='Options, one per line'
          placeholder={PASTE_PLACEHOLDER}
          css={css`
            font-size: 14px !important;
            border-radius: 8px;
            border: 1px solid ${colors.SECONDARY_100};
            background: ${colors.SECONDARY_200};
            color: ${colors.WHITE};
          `}
        />
      )}

      {notes.length > 0 && (
        <Text
          variant='paragraphXSmall'
          weight='regular'
          color={colors.NEUTRAL_50}
        >
          {notes.join(' · ')}
        </Text>
      )}

      {nearDuplicates.length > 0 && (
        <Text
          variant='paragraphXSmall'
          weight='regular'
          color={colors.NEUTRAL_50}
        >
          {nearDuplicates.length === 1
            ? '1 option differs only by capitalization'
            : `${nearDuplicates.length} options differ only by capitalization`}
        </Text>
      )}

      {overCap && (
        <Text
          variant='paragraphXSmall'
          weight='regular'
          color={colors.ERROR_300}
        >
          {`${validOptions.length} options — remove ${
            validOptions.length - MAX_OPTIONS
          } to save (maximum ${MAX_OPTIONS}).`}
        </Text>
      )}

      {overLength.length > 0 && (
        <Text
          variant='paragraphXSmall'
          weight='regular'
          color={colors.ERROR_300}
        >
          {`${overLength.length} option${
            overLength.length === 1 ? ' is' : 's are'
          } longer than ${MAX_OPTION_LABEL_LENGTH} characters and cannot be saved.`}
        </Text>
      )}
    </div>
  )
}

export default OptionEditor
